import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, Inbox, Search } from 'lucide-react';
import {
  REQUEST_STATUSES,
  REQUEST_TYPES,
  type RequestStatus,
  type RequestType,
} from '@geoic/shared';
import { Button } from '@/components/ui/Button.js';
import { Select } from '@/components/ui/Field.js';
import { RequestStatusBadge } from '@/components/ui/Badge.js';
import { useToast } from '@/components/ui/Toast.js';
import { dateTime, price as formatPrice } from '@/lib/format.js';
import { errorMessage } from '@/lib/api-error.js';
import { cn } from '@/lib/cn.js';
import { queryKeys } from '@/lib/query-keys.js';
import { requestSummary } from './request-summary.js';
import {
  REQUEST_STATUS_LABELS,
  REQUEST_TYPE_LABELS,
  requestsApi,
  type RequestListParams,
  type RequestRecord,
} from './requests-api.js';

/** Maketdagi tur nuqtasi ranglari (`D2`, TURI ustuni). */
export const TYPE_DOTS: Record<RequestType, string> = {
  product: 'bg-brand-600',
  cart: 'bg-brand-500',
  general: 'bg-n-400',
  drone_show: 'bg-accent-600',
  repair: 'bg-warning-600',
  course: 'bg-success-600',
  service: 'bg-info-600',
};

/** Yorliqlar — maketdagi tartibda: avval «Hammasi», keyin holatlar. */
const TABS: Array<{ key: string; label: string }> = [
  { key: '', label: 'Hammasi' },
  ...REQUEST_STATUSES.map((status) => ({ key: status, label: REQUEST_STATUS_LABELS[status] })),
];

/**
 * Arizalar ro'yxati — MAKETDAN (`D2 Arizalar.dc.html`).
 *
 * Holat YORLIQLAR bilan tanlanadi (har birida son), tur va qidiruv
 * esa yorliqlar ostidagi filtr qatorida. Kartochka ALOHIDA ekran
 * (`/requests/:id`) — maketda unga «Arizalar ro'yxati» tugmasi bilan
 * qaytiladi.
 *
 * Filtr holati MANZILDA: xodim ro'yxatni hamkasbiga havola qilib
 * yubora oladi va kartochkadan qaytganda filtr saqlanadi.
 */
export function RequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const status = searchParams.get('status') ?? '';
  const type = searchParams.get('type') ?? '';
  const search = searchParams.get('search') ?? '';
  const page = Math.max(1, Number(searchParams.get('page') ?? 1) || 1);
  const [searchDraft, setSearchDraft] = useState(search);

  function patchParams(patch: Record<string, string>): void {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    // Filtr o'zgarsa sahifa boshiga qaytadi — 5-sahifada turib
    // filtrlansa ro'yxat bo'sh chiqib qolardi
    if (!('page' in patch)) next.delete('page');
    setSearchParams(next);
  }

  const params = useMemo<RequestListParams>(
    () => ({
      page,
      limit: 20,
      search: search.trim() || undefined,
      type: (type || undefined) as RequestType | undefined,
      status: (status || undefined) as RequestStatus | undefined,
    }),
    [page, search, type, status],
  );

  const list = useQuery({
    queryKey: queryKeys.requests.list(params),
    queryFn: ({ signal }) => requestsApi.list(params, signal),
  });

  const counts = useQuery({
    queryKey: [...queryKeys.requests.all, 'counts'],
    queryFn: () => requestsApi.counts(),
  });

  const exportFile = useMutation({
    mutationFn: () => requestsApi.exportFile(params),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'arizalar.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = list.data?.data ?? [];
  const total = list.data?.meta.total ?? 0;
  const totalPages = list.data?.meta.totalPages ?? 1;
  const hasFilters = Boolean(status || type || search);

  return (
    <div className="flex flex-col gap-[18px]">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="text-[26px] font-semibold tracking-[-0.02em] text-n-900">Arizalar</h1>
          <p className="mt-1.5 text-[13.5px] text-n-600">
            {list.isLoading ? 'Yuklanmoqda…' : `${total} ta ariza topildi`}
          </p>
        </div>

        <Button
          variant="outline"
          icon={<Download className="size-4" />}
          loading={exportFile.isPending}
          onClick={() => exportFile.mutate()}
        >
          Excel eksport
        </Button>
      </div>

      {/* Holat yorliqlari — maketda pastdan 3px chiziq bilan belgilanadi */}
      <div className="flex gap-0.5 overflow-x-auto border-b border-n-200">
        {TABS.map((tab) => {
          const active = status === tab.key;
          const count = counts.data?.[tab.key || 'all'];

          return (
            <button
              key={tab.key || 'all'}
              type="button"
              aria-pressed={active}
              onClick={() => patchParams({ status: tab.key })}
              className={cn(
                '-mb-px flex items-center gap-2 border-b-[3px] px-4 py-3 text-[13.5px] font-semibold whitespace-nowrap',
                active
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-n-600 hover:text-n-900',
              )}
            >
              {tab.label}
              {count !== undefined && (
                <span
                  className={cn(
                    'min-w-[22px] px-1.5 py-0.5 text-[11.5px] font-bold tabular',
                    active ? 'bg-brand-600 text-white' : 'bg-n-100 text-n-600',
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            patchParams({ search: searchDraft.trim() });
          }}
          className="flex h-[38px] min-w-[240px] flex-1 items-center gap-2.5 border border-n-400 bg-white px-3 sm:max-w-[400px]"
        >
          <Search className="size-4 shrink-0 text-n-600" aria-hidden />
          <label className="sr-only" htmlFor="requests-search">
            Qidirish
          </label>
          <input
            id="requests-search"
            type="search"
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
            placeholder="Ariza raqami, mijoz yoki telefon"
            className="h-full min-w-0 flex-1 border-0 bg-transparent text-[13.5px] outline-none"
          />
        </form>

        <Select
          aria-label="Ariza turi"
          value={type}
          placeholder="Barcha turlar"
          options={REQUEST_TYPES.map((value) => ({ value, label: REQUEST_TYPE_LABELS[value] }))}
          onChange={(event) => patchParams({ type: event.target.value })}
          className="w-44"
        />

        {hasFilters && (
          <button
            type="button"
            onClick={() => {
              setSearchDraft('');
              setSearchParams(new URLSearchParams());
            }}
            className="h-[38px] px-3 text-[13.5px] text-brand-600 underline underline-offset-2"
          >
            Filtrni tozalash
          </button>
        )}
      </div>

      <div className="border border-n-200 bg-white">
        {/* Planshetda jadval sig'maydi — maketda u kartochkalarga aylanadi */}
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b border-n-900 bg-n-50 text-left">
                <th className="px-3.5 py-3 font-semibold whitespace-nowrap">Raqam</th>
                <th className="px-3 py-3 font-semibold whitespace-nowrap">Turi</th>
                <th className="px-3 py-3 font-semibold">Mijoz</th>
                <th className="px-3 py-3 font-semibold">Nima haqida</th>
                <th className="px-3 py-3 text-right font-semibold whitespace-nowrap">Summa</th>
                <th className="px-3 py-3 font-semibold whitespace-nowrap">Holat</th>
                <th className="px-3.5 py-3 text-right font-semibold whitespace-nowrap">Sana</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row._id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                  <td className="px-3.5 py-3 whitespace-nowrap">
                    <Link
                      to={`/requests/${row._id}`}
                      className="font-mono font-medium text-brand-600 hover:text-accent-600"
                    >
                      {row.requestNumber}
                    </Link>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-n-800">
                    <span className="inline-flex items-center gap-2">
                      <span aria-hidden className={cn('size-[7px]', TYPE_DOTS[row.type])} />
                      {REQUEST_TYPE_LABELS[row.type]}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-medium text-n-900">
                    {row.contact.name}
                    <span className="block font-mono text-[12px] tabular text-n-600">
                      {row.contact.phone}
                    </span>
                  </td>
                  <td className="px-3 py-3 leading-snug text-n-600">{requestSummary(row)}</td>
                  <td className="px-3 py-3 text-right font-mono tabular whitespace-nowrap">
                    {row.estimatedAmount === null ? '—' : formatPrice(row.estimatedAmount)}
                  </td>
                  <td className="px-3 py-3">
                    <RequestStatusBadge status={row.status} />
                  </td>
                  <td className="px-3.5 py-3 text-right font-mono text-[12.5px] tabular whitespace-nowrap text-n-600">
                    {dateTime(row.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ul className="lg:hidden">
          {rows.map((row) => (
            <li key={row._id} className="border-b border-n-100 last:border-b-0">
              <Link to={`/requests/${row._id}`} className="flex flex-col gap-2.5 p-4">
                <span className="flex w-full items-center gap-2.5">
                  <span className="font-mono text-[13.5px] font-semibold text-brand-600">
                    {row.requestNumber}
                  </span>
                  <RequestStatusBadge status={row.status} />
                  <span className="ml-auto font-mono text-xs tabular text-n-600">
                    {dateTime(row.createdAt)}
                  </span>
                </span>
                <span className="text-[15px] font-semibold text-n-900">{row.contact.name}</span>
                <span className="text-[13.5px] leading-snug text-n-600">{requestSummary(row)}</span>
                <span className="flex w-full items-center gap-3 border-t border-n-100 pt-2">
                  <span className="inline-flex items-center gap-2 text-[12.5px] text-n-800">
                    <span aria-hidden className={cn('size-[7px]', TYPE_DOTS[row.type])} />
                    {REQUEST_TYPE_LABELS[row.type]}
                  </span>
                  <span className="ml-auto font-mono text-[15px] font-semibold tabular">
                    {row.estimatedAmount === null ? '—' : formatPrice(row.estimatedAmount)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>

        {!list.isLoading && rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
            <Inbox className="size-[34px] text-n-400" aria-hidden />
            <p className="text-lg font-semibold text-n-900">Ariza topilmadi</p>
            <p className="text-sm text-n-600">
              Filtrlarni o‘zgartiring yoki qidiruv so‘zini qisqartiring.
            </p>
          </div>
        )}

        {list.isLoading && (
          <p className="px-6 py-16 text-center text-sm text-n-600">Yuklanmoqda…</p>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-4">
          <span className="text-[13px] text-n-600">
            {page}-sahifa, jami {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => patchParams({ page: String(page - 1) })}
            >
              Oldingi
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => patchParams({ page: String(page + 1) })}
            >
              Keyingi
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export type { RequestRecord };
